/* eslint-disable react/no-access-state-in-setstate */
import * as React from "react";
import { PropTypes } from "prop-types";
import classNames from "classnames";
import kebabCase from "lodash/kebabCase";
import memoize from "lodash/memoize";

import {
  AccordionV1 as OriginalAccordion,
  AccordionItemV1 as AccordionItem
} from "@lululemon/ui-library";
import { createEncodedKey } from "../../../utils/helpers";
import { productEdHandleError } from "../../../utils/product-education-logger";
import { ErrorBoundary } from "@lululemon/mwa-logger";

import ProductEducationAccordionItem from "../product-education-accordion-item/product-education-accordion-item";
import ProductEducationAccordionMediaItem from "../product-education-accordion-media-item/product-education-accordion-media-item";
import IconByName from "../../icon-by-name/icon-by-name";
import { scrollTo as animateScroll } from "../../../common/lib/animate-scroll";
import {
  getMobileNavElement,
  isElementAtTopOfMobileViewPort,
  isMobileNavOpen,
  isElementHidden
} from "../../../utils/common";
// import ProductBadging from "../../product-badging/product-badging";

// import productBadgingStyles from "../../product-badging/product-badging.module.scss";
import "./product-education-accordions.scss";

export class Accordion extends OriginalAccordion {
  constructor(props) {
    super(props);
    const { shouldExpandProductDetails } = props;
    if (shouldExpandProductDetails) {
      // For the WWMT accordion improvements, the product details accordion should be
      // open by default on desktop or larger screens
      this.state.expandedAccordionPanels[1] = true;
    }
    // I don't know of any standard way of checking if mobile nav is open,
    // so I grab a reference to the element once in the constructor.
    this.mobileNavRef = getMobileNavElement();
    this.playPauseButtons = null;
  }

  pauseVideoOnClose() {
    this.playPauseButtons = this.playPauseButtons?.length
      ? this.playPauseButtons
      : document.querySelectorAll(
          '.product-education-accordions [data-testid="play-pause-button"]'
        );
    Array.from(this.playPauseButtons || []).forEach((btn) => {
      if (
        isElementHidden(btn, ".product-education-accordions") &&
        btn?.title === "Pause Video"
      ) {
        btn.click();
      }
    });
  }

  // eslint-disable-next-line class-methods-use-this
  getOpenItemIndex(state = this.state) {
    return Object.values(state?.expandedAccordionPanels)?.reduce(
      (acc, curr, i) => (curr === true ? i : acc),
      -1
    );
  }

  scrollToTopOfAccordion(prevState) {
    const { currentBreakpoint } = this.props;
    const openItem = this.getOpenItemIndex();
    const firstAccordionItem = this.accordionItemsRef[1];
    const isOpenItemChanged = memoize(
      () =>
        JSON.stringify(this.state.expandedAccordionPanels) !==
        JSON.stringify(prevState.expandedAccordionPanels),
      () =>
        Object.values(this.state.expandedAccordionPanels).reduce(
          (acc, curr) => `${acc}_${curr}`,
          ""
        )
    );
    if (
      openItem !== -1 &&
      isOpenItemChanged() &&
      ["small", "mobile", "tablet"].includes(currentBreakpoint) &&
      firstAccordionItem &&
      !isElementAtTopOfMobileViewPort(firstAccordionItem)
    ) {
      try {
        const navHeight =
          this.mobileNavRef?.getBoundingClientRect()?.height || 0;
        const elDistanceToTop =
          window.pageYOffset +
          firstAccordionItem?.getBoundingClientRect().top +
          (isMobileNavOpen() ? -navHeight : 0);
        const scrollElement =
          document.scrollingElement || document.documentElement;
        animateScroll(scrollElement, elDistanceToTop, 400);
      } catch (e) {
        console.error(e);
      }
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (super.componentDidUpdate)
      super.componentDidUpdate(prevProps, prevState, snapshot);
    this.scrollToTopOfAccordion(prevState);
    this.pauseVideoOnClose();
    // istanbul ignore next
    if (
      this.props.fromTarget &&
      this.props.size === "large" &&
      !this.hasBeenSet
    ) {
      this.hasBeenSet = true;
      try {
        // expand the accordions
        const result = Object.entries(
          this.state.expandedAccordionPanels
        )?.reduce(
          (state, panel) => Object.assign(state, { [panel[0]]: true }),
          {}
        );
        this.setState({ expandedAccordionPanels: result });

        // set the height to all of the accordions
        const newHeightState = {};
        const calcHeights = (ref, key, withPx) => {
          const pb = 36;

          if (withPx) {
            newHeightState[key] = ref.getBoundingClientRect().height + pb;
          } else {
            newHeightState[key] =
              ref.getBoundingClientRect().height + parseInt(pb, 10);
          }
        };

        this.accordionPanelContentRef.forEach((ref, key) => {
          calcHeights(ref, key, false);
        });

        const stateSetter = (callback) =>
          this.setState(
            {
              accordionPanelHeights: {
                ...this.state.accordionPanelHeights,
                ...newHeightState
              }
            },
            () => callback
          );
        stateSetter(() => {
          this.accordionPanelContentRef.forEach((ref, key) => {
            if (
              getComputedStyle(ref.parentNode.parentNode).getPropertyValue(
                "max-height"
              ) === "0px"
            ) {
              calcHeights(ref, key, true);
            }
          });
          stateSetter(() => {});
        });
      } catch (e) {
        productEdHandleError(e, "Accordion", "PDP WWMT");
      }
    }
  }
}

export class ProductEducationAccordions extends React.Component {
  constructor(props) {
    super(props);
    this.signpostingBarHeight = 53;
    this.accordionRef = React.createRef();
    this.getAccordionItemRef = this.getAccordionItemRef.bind(this);
    this.getExpandedAccordionPanels =
      this.getExpandedAccordionPanels.bind(this);
    this.handleScrollToOpenAccordion =
      this.handleScrollToOpenAccordion.bind(this);
    this.state = {
      productDetailsMedia: props.productEducation.find(
        (item) => item.media.length > 0
      )?.media
    };
  }

  componentDidUpdate(prevProps) {
    const {
      activeProductEducationAccordionLinkIdx,
      prodEduAccordionLinkHasBeenToggled
    } = this.props;
    const adjustedIdx = ProductEducationAccordions.getAdjustedIndex(
      activeProductEducationAccordionLinkIdx
    );
    const hasAccordionLinkBeenClicked =
      prevProps.prodEduAccordionLinkHasBeenToggled !==
      prodEduAccordionLinkHasBeenToggled;
    const isDesignedForAccordion = adjustedIdx === 0;
    const expandedAccordionPanels = this.getExpandedAccordionPanels();
    const isSelectedAccordionExpanded = expandedAccordionPanels[adjustedIdx];

    if (hasAccordionLinkBeenClicked) {
      if (isDesignedForAccordion || isSelectedAccordionExpanded) {
        this.handleScrollToOpenAccordion();
      }

      if (!isDesignedForAccordion && !isSelectedAccordionExpanded) {
        this.handleOpenAccordionAtIdx(adjustedIdx, {
          shouldScrollToOpenedAccordion: true
        });
      }
    }
  }

  getAccordionItemRef() {
    const { activeProductEducationAccordionLinkIdx, wwmtRef } = this.props;
    const adjustedIdx = ProductEducationAccordions.getAdjustedIndex(
      activeProductEducationAccordionLinkIdx
    );
    const isDesignedForAccordion = adjustedIdx === 0;
    const accordionItemsRefList = this.accordionRef.current.accordionItemsRef;
    const accordionItemRef = isDesignedForAccordion
      ? wwmtRef.current
      : accordionItemsRefList[adjustedIdx];

    return { accordionItemRef, isDesignedForAccordion };
  }

  // offset idx to compensate for 'Designed For,' an extra child
  static getAdjustedIndex(activeProductEducationAccordionLinkIdx) {
    return Number(activeProductEducationAccordionLinkIdx) + 1;
  }

  getExpandedAccordionPanels() {
    if (this.accordionRef) {
      return this.accordionRef.current.state.expandedAccordionPanels;
    }
    return {};
  }

  handleScrollToOpenAccordion() {
    try {
      const { accordionItemRef, isDesignedForAccordion } =
        this.getAccordionItemRef();
      if (!isDesignedForAccordion) {
        // Focus the accordion, which will also bring it into view
        accordionItemRef.focus();
      } else {
        // Note: this solution to scroll to top of PE section
        // should be reviewed in depth for future iterations
        // on the Product Education section
        const el = document.querySelector(
          "[class*='product-education-container'"
        );
        el?.scrollIntoView({ behavior: "smooth" });
      }
    } catch (e) {
      console.error(`There was an error scrolling: ${e}`);
    }
  }

  handleOpenAccordionAtIdx(idx, config = {}) {
    if (this.accordionRef) {
      this.accordionRef.current.triggerOpen(idx, config);
      const { accordionItemRef } = this.getAccordionItemRef();
      accordionItemRef.focus();
    }
  }

  render() {
    const {
      currentBreakpoint,
      useAccordionImprovements,
      designedFor,
      designedForIcon,
      materialAndCare: {
        iconId: careIconId,
        sections: careSections,
        title: careTitle
      },
      productEducation,
      size,
      productDetailsRef
    } = this.props;
    const { productDetailsMedia } = this.state;
    const headingWrapperClassName =
      "wrapper container-fluid product-education-accordions__heading-wrapper";
    const isBreakpointDesktopOrLarger =
      currentBreakpoint === "desktop" ||
      currentBreakpoint === "large" ||
      currentBreakpoint === "xlarge";

    return (
      <ErrorBoundary
        onError={(error) =>
          productEdHandleError(error, "ProductEducationAccordions", "PDP WWMT")
        }
      >
        <div
          className={classNames({
            "product-education-accordions": true,
            [`product-education-accordions--${size}`]: size
          })}
          ref={productDetailsRef}
        >
          <Accordion
            id="product-education-accordions"
            offsetYHeight={this.signpostingBarHeight}
            ref={this.accordionRef}
            shouldScrollToAccordionAfterOpened
            size={size}
            shouldExpandProductDetails={
              useAccordionImprovements && isBreakpointDesktopOrLarger
            }
            currentBreakpoint={currentBreakpoint}
          >
            {!useAccordionImprovements && !!designedFor && (
              <AccordionItem
                accordionId={kebabCase(designedFor)}
                heading={designedFor}
                headingWrapperClassName={headingWrapperClassName}
                icon={
                  !!designedForIcon && <IconByName name={designedForIcon} />
                }
              />
            )}
            {useAccordionImprovements ? (
              <AccordionItem
                accordionId="product-details"
                heading="Product details"
                headingWrapperClassName={headingWrapperClassName}
                icon={<IconByName name="feature_generic" />}
                key="product-education-accordion-item--product-details"
                analyticsId="pdp-accordion-product-details"
              >
                <div className="product-details-container product-education-accordions__panel wrapper container-fluid">
                  <div className="detail-info lll-text-body-1">
                    {productEducation.map((productDetailItem, index) => (
                      <div key={productDetailItem?.id}>
                        <ul
                          className={classNames("lll-font-weight-semibold", {
                            "padding-top": index > 0
                          })}
                        >
                          {productDetailItem?.title}
                        </ul>
                        {productDetailItem?.attributes?.map((itemAttribute) => (
                          <li
                            key={createEncodedKey(itemAttribute)}
                            className="item-attribute-text lll-text-body-2"
                          >
                            {itemAttribute.text}
                          </li>
                        ))}
                        {productDetailItem?.media?.length > 0 &&
                          !isBreakpointDesktopOrLarger && (
                            <div className="mobile-media-item">
                              <ProductEducationAccordionMediaItem
                                accordionIndex={0}
                                media={productDetailItem?.media}
                                currentBreakpoint={currentBreakpoint}
                              />
                            </div>
                          )}
                      </div>
                    ))}
                  </div>

                  {productDetailsMedia && isBreakpointDesktopOrLarger && (
                    <div className="detail-info media">
                      {/* TODO: Video thumbnail size will be increased with: https://lululemon.atlassian.net/browse/CE2-1579 */}
                      <ProductEducationAccordionMediaItem
                        accordionIndex={0}
                        media={productDetailsMedia}
                        currentBreakpoint={currentBreakpoint}
                      />
                    </div>
                  )}
                </div>
              </AccordionItem>
            ) : (
              productEducation.map(
                ({ iconId, title, attributes, media, id }, index) => {
                  const hasValidAttributes =
                    Array.isArray(attributes) && !!attributes.length;
                  const hasValidMedia = Array.isArray(media) && !!media.length;
                  const headingValue = title;
                  const productTrackName = {
                    fabricOrBenefits: "pdp-accordion-fabric",
                    featuresOrHowToUse: "pdp-accordion-product-features",
                    fitOrIngredients: "pdp-accordion-inseam"
                  };
                  const analyticsTrackId =
                    hasValidAttributes && attributes.length > 0
                      ? productTrackName[id]
                      : null;
                  return (
                    <AccordionItem
                      accordionId={kebabCase(title)}
                      heading={headingValue}
                      headingWrapperClassName={headingWrapperClassName}
                      icon={<IconByName name={iconId} />}
                      key={`product-education-accordion-item--${title}`}
                      analyticsId={analyticsTrackId}
                    >
                      {(hasValidAttributes || hasValidMedia) && (
                        <ProductEducationAccordionItem
                          accordionIndex={index}
                          attributes={attributes}
                          currentBreakpoint={currentBreakpoint}
                          media={media}
                          size={size}
                        />
                      )}
                    </AccordionItem>
                  );
                }
              )
            )}
            {!!careTitle && (
              <AccordionItem
                accordionId={kebabCase(careTitle)}
                heading={careTitle}
                headingWrapperClassName={headingWrapperClassName}
                icon={!!careIconId && <IconByName name={careIconId} />}
                key={`product-education-accordion-item--${careTitle}`}
                analyticsId="pdp-accordion-material-and-care"
              >
                {Array.isArray(careSections) &&
                  !!careSections.length &&
                  careSections.map((section) => (
                    <ProductEducationAccordionItem
                      attributes={section.attributes}
                      currentBreakpoint={currentBreakpoint}
                      key={createEncodedKey(section)}
                      media={section.media}
                      size={size}
                      title={section.title}
                    />
                  ))}
                {/* TODO: integrate once ProductBadging is migrated */}
                {/* <ProductBadging
                  className={productBadgingStyles.withMargins}
                  size={size}
                /> */}
              </AccordionItem>
            )}
          </Accordion>
        </div>
      </ErrorBoundary>
    );
  }
}

ProductEducationAccordions.defaultProps = {
  activeProductEducationAccordionLinkIdx: null,
  designedFor: "",
  designedForIcon: "",
  headerBannerHeight: 0,
  materialAndCare: {},
  prodEduAccordionLinkHasBeenToggled: false,
  productEducation: [],
  size: "large",
  wwmtRef: null,
  useAccordionImprovements: false
};

ProductEducationAccordions.propTypes = {
  useAccordionImprovements: PropTypes.bool,
  activeProductEducationAccordionLinkIdx: PropTypes.number,
  currentBreakpoint: PropTypes.string.isRequired,
  designedFor: PropTypes.string,
  designedForIcon: PropTypes.string,
  headerBannerHeight: PropTypes.number,
  materialAndCare: PropTypes.shape({
    iconId: PropTypes.string,
    id: PropTypes.string,
    sections: PropTypes.array,
    title: PropTypes.string
  }),
  prodEduAccordionLinkHasBeenToggled: PropTypes.bool,
  productEducation: PropTypes.arrayOf(PropTypes.object),
  size: PropTypes.string,
  wwmtRef: PropTypes.objectOf(PropTypes.object),
  productDetailsRef: PropTypes.PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any })
  ]).isRequired
};

export default ProductEducationAccordions;
